import { initializePostHogWithConsent } from './utils/posthog';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import NewDesktop from "./pages/NewDesktop";
import NotFound from "./pages/NotFound";
import { AuthProvider } from "./contexts/AuthContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
// Lazy: @react-oauth/google injects accounts.google.com/gsi/client (~80KB)
// on mount. Don't pull it into landing pages.
const GoogleOAuthProvider = lazy(() =>
  import('@react-oauth/google').then((m) => ({ default: m.GoogleOAuthProvider }))
);
// Static imports removed — these are lazy-loaded below
const Vovy = lazyWithRetry(() => import("./pages/Vovy"));
const VovyStart = lazyWithRetry(() => import("./pages/VovyStart"));
import RouteTracker from "./components/analytics/RouteTracker";
import CheckoutReceiptObserver from "./components/analytics/CheckoutReceiptObserver";
import { useExtensionHandoff } from "./hooks/useExtensionHandoff";
// Defer the WebGL2 Rive runtime — never blocks initial paint
const StickyRiveWidget = lazyWithRetry(() => import("./components/ui/StickyRiveWidget"));
import { captureFromUrl as capturePromoSlug } from "./lib/promoSlug";
import { captureAttribution } from "./lib/attribution";

// Lazy-loaded routes for better code splitting
const SignIn = lazyWithRetry(() => import("./pages/SignIn"));
// Post-download landing — where the mac download CTA sends the browser while
// the dmg arrives (install steps + the sign-in step preview).
const DesktopInstalling = lazyWithRetry(() => import("./pages/DesktopInstalling"));
// /get/:token — the short link the "Text me the Mac link" iMessage carries.
const MacLinkLanding = lazyWithRetry(() => import("./pages/MacLinkLanding"));
const NewAuthFlow = lazyWithRetry(() => import("./pages/NewAuthFlow"));
const StatusDiscTest = lazyWithRetry(() => import("./pages/StatusDiscTest"));
const HeroTest = lazyWithRetry(() => import("./pages/HeroTest"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const Docs = lazyWithRetry(() => import("./pages/Docs"));

// Docs moved from /docs to /app/docs — keep old links working.
function DocsLegacyRedirect() {
  const { '*': rest = '' } = useParams();
  const suffix = rest ? `/${rest}` : '';
  return <Navigate to={`/app/docs${suffix}`} replace />;
}
const Studio = lazyWithRetry(() => import("./pages/Studio"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const UseCases = lazyWithRetry(() => import("./pages/UseCases"));
const UseCaseDetail = lazyWithRetry(() => import("./pages/UseCaseDetail"));
const UseCaseRedirect = lazyWithRetry(() =>
  import("./pages/UseCaseDetail").then((m) => ({ default: m.UseCaseRedirect })),
);
const Links = lazyWithRetry(() => import("./pages/Link"));
const Courses = lazyWithRetry(() => import("./pages/Courses"));
const LearnPath = lazyWithRetry(() => import("./pages/LearnPath"));
const Team = lazyWithRetry(() => import("./pages/Team"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const EnterpriseDashboard = lazyWithRetry(() => import("./pages/EnterpriseDashboard"));
const NewLearningDashboard = lazyWithRetry(() => import("./pages/NewLearningDashboard"));
const LearnTools = lazyWithRetry(() => import("./pages/LearnTools"));
const LearnToolCourse = lazyWithRetry(() => import("./pages/LearnToolCourse"));
const LiveLearnHome = lazyWithRetry(() => import("./pages/LiveLearnHome"));
const LiveLearnSession = lazyWithRetry(() => import("./pages/LiveLearnSession"));
const NewDashboardHome = lazyWithRetry(() => import("./pages/NewDashboardHome"));
const Account = lazyWithRetry(() => import("./pages/Account"));
const SuccessPage = lazyWithRetry(() => import("./pages/SuccessPage"));
const ExtensionCheckoutReturn = lazyWithRetry(() => import("./pages/ExtensionCheckoutReturn"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
// GEO/SEO landing pages — real routes, never redirected to "/"
const MacNotchAiAssistant = lazyWithRetry(() => import("./pages/geo/MacNotchAiAssistant"));
const AiAgentFollowsYourCursor = lazyWithRetry(() => import("./pages/geo/AiAgentFollowsYourCursor"));
const BestAiComputerUseAgentForMac = lazyWithRetry(() => import("./pages/geo/BestAiComputerUseAgentForMac"));
const LearningModules = lazyWithRetry(() => import("./pages/LearningModules"));
const ElementPreview = lazyWithRetry(() => import("./pages/ElementPreview"));
const ElementGallery = lazyWithRetry(() => import("./pages/ElementGallery"));

const PromptLabs = lazyWithRetry(() => import("./pages/PromptLabs"));
const DashboardGlossary = lazyWithRetry(() => import("./pages/DashboardGlossary"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const Onboard = lazyWithRetry(() => import("./pages/Onboard"));
const OnboardSuccess = lazyWithRetry(() => import("./pages/OnboardSuccess"));
const AccountOnboard = lazyWithRetry(() => import("./pages/account/Onboard"));
const OnboardGo = lazyWithRetry(() => import("./pages/onboard/OnboardGo"));
const AccountPlans = lazyWithRetry(() => import("./pages/account/Plans"));
const AccountPlanFree = lazyWithRetry(() => import("./pages/account/plan/Free"));
const AccountPlanCheckout = lazyWithRetry(() => import("./pages/account/plan/Checkout"));
const AccountPlanCheckoutSuccess = lazyWithRetry(() => import("./pages/account/plan/CheckoutSuccess"));
const AccountDelete = lazyWithRetry(() => import("./pages/account/DeleteAccount"));
const Demo = lazyWithRetry(() => import("./pages/Demo"));
const Certificate = lazyWithRetry(() => import("./pages/Certificate"));
const Home = lazyWithRetry(() => import("./pages/Home"));
const VovyGo = lazyWithRetry(() => import("./pages/VovyGo"));


const GoCheckout = lazyWithRetry(() => import("./pages/go/GoCheckout"));
const GoIntakePage = lazyWithRetry(() => import("./pages/go/GoIntakePage"));
const Deck = lazyWithRetry(() => import("./pages/Deck"));
const Pitch = lazyWithRetry(() => import("./pages/Pitch"));
const PromoRedirect = lazyWithRetry(() => import("./pages/PromoRedirect"));
const Leaderboard = lazyWithRetry(() => import("./pages/Leaderboard"));
const SkillCertificateDemo = lazyWithRetry(() => import("./pages/SkillCertificateDemo"));
const ClassicalCertificateDemo = lazyWithRetry(() => import("./pages/ClassicalCertificateDemo"));
const Paywall = lazyWithRetry(() => import("./pages/Paywall"));
const Community = lazyWithRetry(() => import("./pages/Community"));
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent"));
const OAuthServerCallback = lazyWithRetry(() => import("./pages/OAuthServerCallback"));
const LearnPlayIndex = lazyWithRetry(() => import("./pages/LearnPlayIndex"));
const LearnPlayPath = lazyWithRetry(() => import("./pages/LearnPlayPath"));
const LearnPlayLesson = lazyWithRetry(() => import("./pages/LearnPlayLesson"));
const LearnCheckout = lazyWithRetry(() => import("./pages/LearnCheckout"));
const EduProgram = lazyWithRetry(() => import("./pages/EduProgram"));
const Fable = lazyWithRetry(() => import("./pages/Fable"));
const PublicPreview = lazyWithRetry(() => import("./pages/PublicPreview"));
const FirstBuildPage = lazyWithRetry(() => import("./pages/FirstBuildPage"));
const Welcome = lazyWithRetry(() => import("./pages/Welcome"));
import Open from "@/pages/Open";


// Blog posts - lazy loaded
const WhatIsVibeBlog = lazyWithRetry(() => import("./pages/Blog/what-is-vibe-coding"));
const LearnToVibeBlog = lazyWithRetry(() => import("./pages/Blog/learn-to-vibe"));
const TagVibesBlog = lazyWithRetry(() => import("./pages/Blog/tag-vibes"));
const Builders2025Blog = lazyWithRetry(() => import("./pages/Blog/builders-2025"));
const EliQuitStartupBlog = lazyWithRetry(() => import("./pages/Blog/eli-quit-startup"));
const JackFounderJourneyBlog = lazyWithRetry(() => import("./pages/Blog/jack-founder-journey"));
const Top5ToolsBlog = lazyWithRetry(() => import("./pages/Blog/top-5-tools"));
const FiveTypesAppsBlog = lazyWithRetry(() => import("./pages/Blog/5-types-apps"));
const LearningToBuildBlog = lazyWithRetry(() => import("./pages/Blog/learning-to-build"));
const VibeCodingFoundersBlog = lazyWithRetry(() => import("./pages/Blog/vibe-coding-founders"));
const IdeaToAppWeekendBlog = lazyWithRetry(() => import("./pages/Blog/idea-to-app-weekend"));
const NoCodeAIStartupsBlog = lazyWithRetry(() => import("./pages/Blog/no-code-ai-startups"));
const PromptEngineeringFoundersBlog = lazyWithRetry(() => import("./pages/Blog/prompt-engineering-founders"));
const VibeCodingStackBlog = lazyWithRetry(() => import("./pages/Blog/vibe-coding-stack"));
const AICofounderBlog = lazyWithRetry(() => import("./pages/Blog/ai-cofounder"));
const LearnVibeCodingBlog = lazyWithRetry(() => import("./pages/Blog/learn-vibe-coding"));
const WhyAIAppCrashedBlog = lazyWithRetry(() => import("./pages/Blog/why-ai-app-crashed"));
const ChatGPT56GuideBlog = lazyWithRetry(() => import("./pages/Blog/chatgpt-5-6-guide"));
const ComputerUseAgentsVsChatbotsBlog = lazyWithRetry(() => import("./pages/Blog/computer-use-agents-vs-chatbots"));
const WhatIsAComputerUseAgentBlog = lazyWithRetry(() => import("./pages/Blog/what-is-a-computer-use-agent"));
const IsComputerUseAISafeBlog = lazyWithRetry(() => import("./pages/Blog/is-computer-use-ai-safe"));
const ClaudeComputerUseUpdateBlog = lazyWithRetry(() => import("./pages/Blog/claude-computer-use-update"));

// Initialize the analytics adapter immediately; calls made while the SDK loads
// are queued in order, including the initial landing page and auth identity.
if (typeof window !== 'undefined') initializePostHogWithConsent();

// Capture promo slug from URL (?promo=iu) once at app boot. Persists in
// sessionStorage so later checkout calls can forward it to the server-side
// whitelist. Safe to run on every page load — idempotent.
try {
  capturePromoSlug();
} catch (e) {
  console.warn('Promo slug capture failed:', e);
}

// Capture ad attribution (UTMs, gclid/fbclid, referrer) once at boot —
// first/last-touch persisted in localStorage, registered as Mixpanel super
// props, written to the user row at signup. See src/lib/attribution.ts.
try {
  captureAttribution();
} catch (e) {
  console.warn('Attribution capture failed:', e);
}

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => {
  // Use white background for dashboard routes to prevent flash
  const isDashboard = window.location.pathname.startsWith('/dashboard') || window.location.pathname.startsWith('/app');
  return (
    <div className={`min-h-screen ${isDashboard ? 'bg-background' : 'bg-gradient-to-b from-white to-blue-50'}`} />
  );
};

// '/' (the desktop landing page) is excluded: it already flies its own Vovy
// via VovyGuide, so the generic sticky widget would double up.
const RIVE_WIDGET_ROUTES = ['/learn', '/posts', '/team'];

function ConditionalRiveWidget() {
  const location = useLocation();
  const allowed =
    RIVE_WIDGET_ROUTES.includes(location.pathname) ||
    location.pathname.startsWith('/app');
  if (!allowed) return null;
  // Inside a lesson player (/app/learn/:pathSlug/:lessonSlug) the
  // floating Vovy chat is hidden on mobile so it doesn't cover the lesson UI.
  const seg = location.pathname.split('/').filter(Boolean);
  const inLesson = seg[0] === 'app' && seg[1] === 'learn' && seg.length >= 4;
  return (
    <Suspense fallback={null}>
      {inLesson ? <div className="hidden md:block"><StickyRiveWidget /></div> : <StickyRiveWidget />}
    </Suspense>
  );
}

// Global OAuth callback handler component
const OAuthRedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip if already on auth callback route
    if (location.pathname === '/auth/callback') return;
    
    // Check if URL has OAuth response in hash (implicit flow from Supabase)
    const hash = window.location.hash;
    if (hash && (hash.includes('access_token') || hash.includes('error'))) {
      console.log('🔄 Detected OAuth response in hash, redirecting to /auth/callback');
      // Preserve the hash and redirect to callback route
      navigate('/auth/callback' + hash, { replace: true });
    }
  }, [location, navigate]);

  return null;
};

// Mounts the cookie + postMessage bridge so the Vovy browser extension can
// auto-detect a signed-in user on *.vovy.ai and request a short-lived handoff.
const ExtensionHandoffBridge = () => {
  useExtensionHandoff();
  return null;
};

// Only mount Google's OAuth provider (which injects gsi/client) on routes that
// actually use Google sign-in. Saves ~80KB + a 3rd-party connection on landing.
const GOOGLE_AUTH_ROUTE_PREFIXES = [
  '/signin', '/signup', '/onboard', '/auth', '/oauth',
  '/account', '/dashboard', '/forgot-password', '/reset-password',
];

function MaybeGoogleProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const needs = GOOGLE_AUTH_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
  if (!needs) return <>{children}</>;
  return (
    <Suspense fallback={<>{children}</>}>
      <GoogleOAuthProvider clientId="815239478210-rtr78h9smgiotfh36a30kfrui4ltbj7u.apps.googleusercontent.com">
        {children}
      </GoogleOAuthProvider>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <OrganizationProvider>
      <TooltipProvider>
        <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 overflow-x-clip">
          <Sonner />
          <BrowserRouter>
            <MaybeGoogleProvider>
              <OAuthRedirectHandler />
              <RouteTracker />
              <CheckoutReceiptObserver />
              <ExtensionHandoffBridge />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<NewDesktop />} />
                  {/* Task library: directory + one programmatic page per task */}
                  <Route path="/tasks" element={<UseCases />} />
                  <Route path="/tasks/:slug" element={<UseCaseDetail />} />
                  <Route path="/use-cases" element={<Navigate to="/tasks" replace />} />
                  <Route path="/use-cases/:slug" element={<UseCaseRedirect />} />
                  {/* Where a mac download click lands while the dmg downloads */}
                  <Route path="/desktop/installing" element={<DesktopInstalling />} />
                  <Route path="/get/:token" element={<MacLinkLanding />} />
                  <Route path="/desktop" element={<Navigate to="/" replace />} />
                  {/* /newdesktop was where this page was built; it is the home
                      page now, so the old slug redirects rather than 404s */}
                  <Route path="/newdesktop" element={<Navigate to="/" replace />} />
                  <Route path="/go" element={<VovyGo />} />
                  
                  {/* /learn briefly served the education home — keep old links working */}
                  <Route path="/learn" element={<Home />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/learn/:pathSlug" element={<LearnPath />} />
                  <Route path="/learn/play" element={<LearnPlayIndex />} />
                  <Route path="/learn/play/:pathSlug" element={<LearnPlayPath />} />
                  <Route path="/learn/play/:pathSlug/:lessonSlug" element={<LearnPlayLesson />} />
                  <Route path="/learn/checkout" element={<LearnCheckout />} />
                  <Route path="/__el/:id" element={<ElementPreview />} />
                  <Route path="/__gallery" element={<ElementGallery />} />
                  <Route path="/__gallery/:file" element={<ElementGallery />} />
                  <Route path="/status-disc-test" element={<StatusDiscTest />} />
                  <Route path="/herotest" element={<HeroTest />} />

                  <Route path="/onboard" element={<Onboard />} />
                  <Route path="/onboard/success" element={<OnboardSuccess />} />
                  <Route path="/account/onboard" element={<AccountOnboard />} />
                  <Route path="/account/onboard/choose-plan" element={<AccountOnboard />} />
                  <Route path="/account/onboard/checkout" element={<AccountOnboard />} />
                  <Route path="/account/onboard/complete" element={<AccountOnboard />} />
                  {/* /onboard/welcome (prod slug) now serves the NEW onboarding (NewAuthFlow).
                      Legacy /onboard/pick + /onboard/learn redirect here. /onboard/go kept for extension OAuth consent. */}
                  <Route path="/onboard/welcome" element={<NewAuthFlow />} />
                  <Route path="/onboard/pick" element={<Navigate to="/onboard/welcome" replace />} />
                  <Route path="/onboard/learn" element={<Navigate to="/onboard/welcome" replace />} />
                  <Route path="/onboard/go" element={<OnboardGo />} />
                  <Route path="/account/plans" element={<AccountPlans />} />
                  <Route path="/account/plan/free" element={<AccountPlanFree />} />
                  <Route path="/account/plan/:planType/checkout" element={<AccountPlanCheckout />} />
                  <Route path="/account/plan/:planType/checkout/success" element={<AccountPlanCheckoutSuccess />} />
                  <Route path="/account/delete" element={<AccountDelete />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/fable" element={<Fable />} />
                  <Route path="/mac-notch-ai-assistant" element={<MacNotchAiAssistant />} />
                  <Route path="/ai-agent-that-follows-your-cursor" element={<AiAgentFollowsYourCursor />} />
                  <Route path="/best-ai-computer-use-agent-for-mac" element={<BestAiComputerUseAgentForMac />} />
                  <Route path="/plans/start" element={<Paywall />} />

                  <Route path="/team" element={<Team />} />
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/signup" element={<SignIn />} />
                  <Route path="/newauthflow" element={<NewAuthFlow />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/oauth/consent" element={<OAuthConsent />} />
                  <Route path="/oauth/callback" element={<OAuthServerCallback />} />
                  <Route path="/app/docs/*" element={<Docs />} />
                  <Route path="/docs/*" element={<DocsLegacyRedirect />} />
                  <Route path="/posts" element={<Blog />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/vibe-code" element={<WhatIsVibeBlog />} />
                  <Route path="/blog/learn-to-vibe" element={<LearnToVibeBlog />} />
                  <Route path="/blog/tag-vibes" element={<TagVibesBlog />} />
                  <Route path="/blog/builders-2025" element={<Builders2025Blog />} />
                  <Route path="/blog/eli-quit-startup" element={<EliQuitStartupBlog />} />
                  <Route path="/blog/jack-founder-journey" element={<JackFounderJourneyBlog />} />
                  <Route path="/blog/top-5-tools" element={<Top5ToolsBlog />} />
                  <Route path="/blog/5-types-apps" element={<FiveTypesAppsBlog />} />
                  <Route path="/blog/learning-to-build" element={<LearningToBuildBlog />} />
                  <Route path="/blog/vibe-coding-founders" element={<VibeCodingFoundersBlog />} />
                  <Route path="/blog/idea-to-app-weekend" element={<IdeaToAppWeekendBlog />} />
                  <Route path="/blog/no-code-ai-startups" element={<NoCodeAIStartupsBlog />} />
                  <Route path="/blog/prompt-engineering-founders" element={<PromptEngineeringFoundersBlog />} />
                  <Route path="/blog/vibe-coding-stack" element={<VibeCodingStackBlog />} />
                  <Route path="/blog/ai-cofounder" element={<AICofounderBlog />} />
                  <Route path="/blog/learn-vibe-coding" element={<LearnVibeCodingBlog />} />
                  <Route path="/blog/why-ai-app-crashed" element={<WhyAIAppCrashedBlog />} />
                  <Route path="/blog/chatgpt-5-6-guide" element={<ChatGPT56GuideBlog />} />
                  <Route path="/blog/computer-use-agents-vs-chatbots" element={<ComputerUseAgentsVsChatbotsBlog />} />
                  <Route path="/blog/what-is-a-computer-use-agent" element={<WhatIsAComputerUseAgentBlog />} />
                  <Route path="/blog/is-computer-use-ai-safe" element={<IsComputerUseAISafeBlog />} />
                  <Route path="/blog/claude-computer-use-update" element={<ClaudeComputerUseUpdateBlog />} />
                  <Route path="/vovy/chat" element={<Studio />} />
                  <Route path="/links" element={<Links />} />
                  <Route path="/dashboard" element={<Navigate to="/app/home" replace />} />
                  {/* Vovy Go dashboard section removed — legacy links land on Home. */}
                  <Route path="/dashboard/go" element={<Navigate to="/app/home" replace />} />
                  <Route path="/dashboard/go/plan" element={<Navigate to="/app/home" replace />} />
                  <Route path="/dashboard/go/skills" element={<Navigate to="/app/home" replace />} />
                  <Route path="/enterprisedashboard" element={<Navigate to="/enterprisedashboard/overview" replace />} />
                  <Route path="/enterprisedashboard/overview" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/usage" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/projects" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/learning-overview" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/learning" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/prompt-labs" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/key-terms" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/modules" element={<Navigate to="/enterprisedashboard/learning" replace />} />
                  <Route path="/enterprisedashboard/skills" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/skills/:slug" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/cost" element={<Navigate to="/enterprisedashboard/usage" replace />} />
                  <Route path="/enterprisedashboard/team" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/governance" element={<EnterpriseDashboard />} />
                  <Route path="/enterprisedashboard/insights" element={<Navigate to="/enterprisedashboard/overview" replace />} />
                  <Route path="/app" element={<Navigate to="/app/home" replace />} />
                  <Route path="/app/home" element={<NewDashboardHome />} />
                  <Route path="/app/learn" element={<NewLearningDashboard />} />
                  <Route path="/app/learn/tools" element={<LearnTools />} />
                  <Route path="/app/learn/tools/:toolId" element={<LearnToolCourse />} />
                  <Route path="/app/learn/live" element={<LiveLearnHome />} />
                  <Route path="/app/learn/live/session/:sessionId" element={<LiveLearnSession />} />
                  <Route path="/app/learn/checkout" element={<LearnCheckout />} />
                  <Route path="/app/learn/:pathSlug" element={<LearnPlayPath basePath="/app/learn" />} />
                  <Route path="/app/learn/:pathSlug/:lessonSlug" element={<LearnPlayLesson basePath="/app/learn" />} />
                  <Route path="/app/first-build" element={<FirstBuildPage />} />
                  {/* Post-purchase Mac-app-first handoff (mints ext-session, deep-links vovy://). noindex. */}
                  <Route path="/welcome" element={<Welcome />} />
                  {/* Email-safe app launcher: emails can't carry vovy:// links
                      (Gmail strips custom schemes), so they link here and this
                      page does the scheme jump with a download fallback. */}
                  <Route path="/open" element={<Open />} />
                  {/* New-dashboard surfaces, unified under /app/* */}
                  <Route path="/app/prompt" element={<PromptLabs />} />
                  <Route path="/app/prompt/:chapter" element={<PromptLabs />} />
                  <Route path="/app/prompt/:chapter/:lessonId" element={<PromptLabs />} />
                  <Route path="/app/glossary" element={<DashboardGlossary />} />
                  <Route path="/app/chat" element={<Studio />} />
                  <Route path="/app/leaderboard" element={<Leaderboard />} />
                  <Route path="/app/docs/*" element={<Docs />} />
                  <Route path="/dashboard/go/project/:projectUrlEncoded" element={<Navigate to="/app/home" replace />} />
                  <Route path="/dashboard/go/skills/:skillId" element={<Navigate to="/app/home" replace />} />
                  <Route path="/dashboard/glossary" element={<Navigate to="/app/glossary" replace />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/community" element={<Community />} />
                  <Route path="/settings" element={<Account />} />
                  <Route path="/account/billing" element={<Account />} />
                  <Route path="/success" element={<SuccessPage />} />
                  <Route path="/extension-checkout-return" element={<ExtensionCheckoutReturn />} />
                  <Route path="/vibe-path" element={<LearningModules />} />
                  
                  <Route path="/vovy/prompt" element={<PromptLabs />} />
                  <Route path="/vovy/prompt/:chapter" element={<PromptLabs />} />
                  <Route path="/vovy/prompt/:chapter/:lessonId" element={<PromptLabs />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/certificate/:certificateId" element={<Certificate />} />
                  <Route path="/demo/*" element={<Demo />} />
                  <Route path="/skill-certificate" element={<SkillCertificateDemo />} />
                  <Route path="/classical-certificate" element={<ClassicalCertificateDemo />} />
                  <Route path="/vovy/start" element={<VovyStart />} />
                  <Route path="/vovy/demo" element={<Navigate to="/onboard/welcome" replace />} />
                  <Route path="/vovy/learn/:pathSlug" element={<Vovy />} />
                  <Route path="/vovy" element={<Vovy />} />
                  {/* shared standalone checkout/intake infra for Vovy Go credits —
                      used by Pricing, Learn, OnboardingPaywall, and onboarding
                      (not just a single marketing page), so it stays even though
                      the /go marketing route itself is gone. */}
                  <Route path="/plan/new" element={<GoIntakePage />} />
                  <Route path="/checkout" element={<GoCheckout />} />
                  <Route path="/pitch" element={<Pitch />} />
                  {/* First Build public share page — no auth, no app shell.
                      See .claude/SHOWCASE-BUILD-MAP.md. */}
                  <Route path="/p/:slug" element={<PublicPreview />} />
                  {/* Vovy Edu — vovy.ai/edu/<school>: verify .edu email → semester pass → certificate */}
                  <Route path="/edu/:school" element={<EduProgram />} />
                  {/* Promo slugs — /iu (and future short codes) redirect to
                      /signup with ?promo=<slug> so the discount auto-applies. */}
                  <Route path="/iu" element={<PromoRedirect />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <ConditionalRiveWidget />
            </MaybeGoogleProvider>
          </BrowserRouter>
        </div>
      </TooltipProvider>
      </OrganizationProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
